// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '../../../DataCache';
import { Account } from '../../../cache/Account';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { RiskManagementGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { BaseAccountGroupController } from '../BaseControllers/BaseAccountGroupController';
import { Resources } from '@shared/localizations/Resources';
import { RowItemDataNumberArray, RowItemDataStringArray } from '../../../cache/VerticalPanel/AccountDetails/RowItemData';
import { VerticalPanelColoringTypes } from '../../VerticalPanelColoringTypes';
import { RiskPlanHelper } from '@shared/utils/RiskPlan/RiskPlanHelper';
import { MarginTypes } from '@shared/utils/Instruments/MarginTypes';
import { getProductTypeKey, ProductType } from '@shared/utils/Instruments/ProductType';
import { VolumeLimitsHelper } from '@shared/utils/Account/VolumeLimitsHelper';
import { GROUP } from '@shared/utils/Rules/AccountRules';
export class RiskManagementGroupController extends BaseAccountGroupController {
    constructor() {
        super(...arguments);
        this._riskPlanHelper = new RiskPlanHelper();
        this._volumeLimitsHelper = new VolumeLimitsHelper();
        this.maxPositionValueMatch = (instrumentGroupRiskPlan, riskPlanItem) => {
            if (instrumentGroupRiskPlan.isSuperGroup) {
                return (isValidNumber(riskPlanItem.MaxPositionValuePerSuperGroup) &&
                    riskPlanItem.MaxPositionValuePerSuperGroup !== -1);
            }
            else {
                return (isValidNumber(riskPlanItem.MaxPositionValuePerSymbolGroup) &&
                    riskPlanItem.MaxPositionValuePerSymbolGroup !== -1);
            }
        };
    }
    getGroupItemsTypes() {
        return [
            RiskManagementGroupEnum.CurrentStatus,
            // RiskManagementGroupEnum.AllowTradingPrePost,
            RiskManagementGroupEnum.DailyLoss_MaxDailyLossLimit,
            RiskManagementGroupEnum.DailyLoss_RelativeDailyLossLimit,
            RiskManagementGroupEnum.CurrentWeeklyLoss_MaxWeekLostLimit,
            RiskManagementGroupEnum.UnrealizedLoss_UnrealizedLossLimit,
            RiskManagementGroupEnum.ProjectedBalance_TrailingDrawdownLevel,
            RiskManagementGroupEnum.CurrentTotalPositionQty_TotalMaxPositionsQty,
            RiskManagementGroupEnum.CurrentOrdersCountPerDay_MaxOrdersPerDay,
            RiskManagementGroupEnum.CurrentPositions_MaxPositions,
            RiskManagementGroupEnum.CurrentPendingOrders_MaxPendingOrders,
            // RiskManagementGroupEnum.MaxPendingOrdersValue,
            RiskManagementGroupEnum.MaxOrderCapital,
            RiskManagementGroupEnum.ProjectedBalance_MaxDrawdownLevel,
            // RiskManagementGroupEnum.MaximumLot,
            // RiskManagementGroupEnum.MaxPositionQtyPerSymbol,
            RiskManagementGroupEnum.AllowOvernightTrading,
            RiskManagementGroupEnum.MaxDailyProfit,
            RiskManagementGroupEnum.MaxOrderAmount,
            RiskManagementGroupEnum.DayTraderPatternProtection,
            RiskManagementGroupEnum.AvailableDayTrades,
            RiskManagementGroupEnum.ProjectedBalance_MaxRelativeDrawDownLevel,
            RiskManagementGroupEnum.LossLimitPerTrade,
            RiskManagementGroupEnum.EODTrailingDrawdownLevel
        ];
    }
    getDynamicItems() {
        const dynamicItems = [];
        //MaxPositionValuePerSymbol
        const items = this._riskPlanHelper.createInstrumentGroupsRiskPlanByMarginType(this.Account.RiskPlan, MarginTypes.TIERED_BY_GROUP);
        dynamicItems.push(...this.createDynamicItemsFromInstrumentGroupItems(RiskManagementGroupEnum.MaxPositionValuePerSymbol, items, this.maxPositionValueMatch));
        //VolumeLimit
        const volumeLimitsInstrumentTypes = this._volumeLimitsHelper.getInstrumentTypes(this.Account);
        dynamicItems.push(...this.createDynamicItemsFromInstrumentType(RiskManagementGroupEnum.VolumeLimit, volumeLimitsInstrumentTypes));
        return dynamicItems;
    }
    getItemValue(type, key) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        switch (type) {
            case RiskManagementGroupEnum.CurrentStatus:
                return new RowItemDataNumberArray(NaN);
            case RiskManagementGroupEnum.AllowTradingPrePost:
                return new RowItemDataNumberArray(NaN);
            case RiskManagementGroupEnum.DailyLoss_MaxDailyLossLimit:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.CurrentDailyLoss, curAccount, assetBalance), curAccount.MaxDailyLossLimit);
            case RiskManagementGroupEnum.CurrentOrdersCountPerDay_MaxOrdersPerDay:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.OpenOrdersNumber, curAccount, assetBalance), DataCache.getRuleNumberValueForAccount(RulesSet.VALUE_MAX_DAY_ORDERS, curAccount, -1));
            case RiskManagementGroupEnum.CurrentPositions_MaxPositions:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.OpenPostionsNumber, curAccount, assetBalance), curAccount.MaxPositions >= 0 ? curAccount.MaxPositions : 0);
            case RiskManagementGroupEnum.CurrentPendingOrders_MaxPendingOrders:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.OpenOrdersNumber, curAccount, assetBalance), curAccount.MaxPendingOrders >= 0 ? curAccount.MaxPendingOrders : 0);
            case RiskManagementGroupEnum.MaxPendingOrdersValue:
                return new RowItemDataNumberArray(NaN);
            case RiskManagementGroupEnum.MaxOrderCapital:
                return new RowItemDataNumberArray(curAccount.MaxOrderCapital !== -1 ? curAccount.MaxOrderCapital : 0);
            case RiskManagementGroupEnum.ProjectedBalance_MaxDrawdownLevel:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.ProjectedBalance, curAccount, assetBalance), curAccount.MaxDrawdownLevel);
            case RiskManagementGroupEnum.MaximumLot:
                return new RowItemDataNumberArray(NaN);
            case RiskManagementGroupEnum.MaxPositionQtyPerSymbol:
                return new RowItemDataNumberArray(NaN);
            case RiskManagementGroupEnum.AllowOvernightTrading:
                return new RowItemDataNumberArray(curAccount.AllowOvernightTrading);
            case RiskManagementGroupEnum.ProjectedBalance_TrailingDrawdownLevel:
                if (curAccount.TrailingDrawdownLevel === -1) {
                    return new RowItemDataNumberArray(0, -1);
                }
                else {
                    return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.ProjectedBalance, curAccount, assetBalance), curAccount.TrailingDrawdownLevel);
                }
            case RiskManagementGroupEnum.CurrentTotalPositionQty_TotalMaxPositionsQty:
                return new RowItemDataNumberArray(curAccount.OpenPosAmount, curAccount.TotalMaxPositionsQty);
            case RiskManagementGroupEnum.CurrentWeeklyLoss_MaxWeekLostLimit: {
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    return new RowItemDataNumberArray(0, 0);
                }
                else {
                    return new RowItemDataNumberArray(curAccount.CurrentWeeklyLoss, curAccount.MaxWeeklyLossLimit);
                }
            }
            case RiskManagementGroupEnum.UnrealizedLoss_UnrealizedLossLimit:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    return new RowItemDataNumberArray(0);
                }
                else {
                    return new RowItemDataNumberArray(curAccount.ProfitBase > 0 ? 0 : -curAccount.ProfitBase, curAccount.MaxUrealizedLossLimit);
                }
            case RiskManagementGroupEnum.MaxDailyProfit: {
                const currentDailyProfit = Account.GetAccountFeature(AccountFeature.CurrentDailyProfit, curAccount, assetBalance);
                const maxDailyProfit = DataCache.getRuleNumberValueForAccount(RulesSet.VALUE_DAILY_PROFIT_TARGET, curAccount, -1);
                return new RowItemDataNumberArray(currentDailyProfit, maxDailyProfit);
            }
            case RiskManagementGroupEnum.VolumeLimit: {
                const parsedKey = this.parseInstrumentTypeItemKey(key);
                if (isNullOrUndefined(parsedKey)) {
                    return new RowItemDataNumberArray(NaN, NaN);
                }
                const limitValues = this._volumeLimitsHelper.getLimitValues(this.Account, this.AssetBalance, parsedKey);
                return new RowItemDataNumberArray(limitValues.currentValue, limitValues.limitValue);
            }
            case RiskManagementGroupEnum.MaxOrderAmount:
                return new RowItemDataNumberArray(curAccount.MaxOrderAmount);
            case RiskManagementGroupEnum.DayTraderPatternProtection:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.DayTraderPatternProtection, curAccount, assetBalance));
            case RiskManagementGroupEnum.AvailableDayTrades:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.AvailableDayTrades, curAccount, assetBalance));
            case RiskManagementGroupEnum.ProjectedBalance_MaxRelativeDrawDownLevel:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.ProjectedBalance, curAccount, assetBalance), Account.GetAccountFeature(AccountFeature.MaxRelativeDrawDownLevel, curAccount, assetBalance));
            case RiskManagementGroupEnum.LossLimitPerTrade:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.LossLimitPerTrade, curAccount, assetBalance));
            case RiskManagementGroupEnum.EODTrailingDrawdownLevel:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.EODTrailingDrawdownLevel, curAccount, assetBalance));
            case RiskManagementGroupEnum.DailyLoss_RelativeDailyLossLimit:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.CurrentDailyLoss, curAccount, assetBalance), Account.GetAccountFeature(AccountFeature.RelativeDailyLossLimit, curAccount, assetBalance));
            case RiskManagementGroupEnum.MaxPositionValuePerSymbol: {
                const parsedKey = this.parseInstrumentGroupRiskPlanItemKey(key);
                const riskPlanItem = !isNullOrUndefined(parsedKey)
                    ? this._riskPlanHelper.findRiskPlanItem(this.Account.RiskPlan, MarginTypes.TIERED_BY_GROUP, parsedKey.routeId, parsedKey.instrumentGroupId, parsedKey.productType)
                    : null;
                if (!isNullOrUndefined(riskPlanItem)) {
                    const positionValue = this.AssetBalance.getPositionValue(parsedKey.routeId, parsedKey.instrumentGroupId, parsedKey.productType);
                    let currentValue = 0;
                    let totalValue = 0;
                    if (positionValue != null) {
                        currentValue = parsedKey.isSuperGroup
                            ? positionValue.current.perSuperGroup
                            : positionValue.current.perInstrumentGroup;
                    }
                    if (parsedKey.isSuperGroup && isValidNumber(riskPlanItem.MaxPositionValuePerSuperGroup)) {
                        totalValue = riskPlanItem.MaxPositionValuePerSuperGroup;
                    }
                    else if (!parsedKey.isSuperGroup && isValidNumber(riskPlanItem.MaxPositionValuePerSymbolGroup)) {
                        totalValue = riskPlanItem.MaxPositionValuePerSymbolGroup;
                    }
                    return new RowItemDataNumberArray(currentValue, totalValue);
                }
                else {
                    return new RowItemDataNumberArray(NaN, NaN);
                }
            }
            default:
                return new RowItemDataNumberArray(NaN);
        }
    }
    getItemFormattedValue(type, key) {
        var _a;
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        const value = this.getItemValue(type, key);
        if (!value.isValid()) {
            return new RowItemDataStringArray('');
        }
        switch (type) {
            case RiskManagementGroupEnum.CurrentStatus:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(null, AccountFeature.CurrentTradingStatus, curAccount, assetBalance));
            case RiskManagementGroupEnum.AllowTradingPrePost:
                return new RowItemDataStringArray('');
            case RiskManagementGroupEnum.DailyLoss_MaxDailyLossLimit:
                if (value.secondValue === -1) {
                    return new RowItemDataStringArray('');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue), assetBalance.formatPrice(value.secondValue));
                }
            case RiskManagementGroupEnum.CurrentOrdersCountPerDay_MaxOrdersPerDay:
                if (value.secondValue === -1) {
                    return new RowItemDataStringArray('');
                }
                else {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.OpenOrdersNumber, curAccount, assetBalance), value.secondValue > 0 ? value.secondValue.toString() : '');
                }
            case RiskManagementGroupEnum.CurrentPositions_MaxPositions:
                if (value.secondValue > 0) {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.OpenPostionsNumber, curAccount, assetBalance), value.secondValue.toString());
                }
                else {
                    return new RowItemDataStringArray('', '');
                }
            case RiskManagementGroupEnum.CurrentPendingOrders_MaxPendingOrders:
                if (value.secondValue > 0) {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.OpenOrdersNumber, curAccount, assetBalance), value.secondValue.toString());
                }
                else {
                    return new RowItemDataStringArray('');
                }
            case RiskManagementGroupEnum.MaxPendingOrdersValue:
                return new RowItemDataStringArray('');
            case RiskManagementGroupEnum.MaxOrderCapital:
                if (value.firstValue <= 0) {
                    return new RowItemDataStringArray('');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue));
                }
            case RiskManagementGroupEnum.MaximumLot:
                return new RowItemDataStringArray('');
            case RiskManagementGroupEnum.MaxPositionQtyPerSymbol:
                return new RowItemDataStringArray('');
            case RiskManagementGroupEnum.AllowOvernightTrading:
                return new RowItemDataStringArray(Resources.getResource(value.firstValue
                    ? 'panel.accounts.accountDetails.RiskManagement.Trading.State.Enabled'
                    : 'panel.accounts.accountDetails.RiskManagement.Trading.State.Disabled'));
            case RiskManagementGroupEnum.ProjectedBalance_TrailingDrawdownLevel:
                if (curAccount.TrailingDrawdownLevel === -1) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.ProjectedBalance, curAccount, assetBalance), assetBalance.formatPrice(value.secondValue));
                }
            case RiskManagementGroupEnum.CurrentTotalPositionQty_TotalMaxPositionsQty:
                if (curAccount.TotalMaxPositionsQty === -1) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(value.firstValue.toString(), value.secondValue.toString());
                }
            case RiskManagementGroupEnum.DailyLoss_RelativeDailyLossLimit:
                if (value.secondValue === null) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue), Account.GetAccountFeatureString(value.secondValue, AccountFeature.RelativeDailyLossLimit, curAccount, assetBalance));
                }
            case RiskManagementGroupEnum.CurrentWeeklyLoss_MaxWeekLostLimit: {
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue), assetBalance.formatPrice(value.secondValue));
                }
            }
            case RiskManagementGroupEnum.UnrealizedLoss_UnrealizedLossLimit:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue), assetBalance.formatPrice(value.secondValue)); // assetBalance.formatPrice(curAccount.MaxUrealizedLossLimit));
                }
            case RiskManagementGroupEnum.MaxDailyProfit:
                if (value.secondValue === -1) {
                    return new RowItemDataStringArray('');
                }
                else if (value.secondValue == null) {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue));
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue), assetBalance.formatPrice(value.secondValue));
                }
            case RiskManagementGroupEnum.VolumeLimit: {
                const parsedKey = this.parseInstrumentTypeItemKey(key);
                if (isNullOrUndefined(parsedKey)) {
                    return new RowItemDataStringArray('');
                }
                const limitValuesStr = this._volumeLimitsHelper.getLimitValuesStr(this.Account, this.AssetBalance, parsedKey);
                return new RowItemDataStringArray(limitValuesStr.currentValueStr, limitValuesStr.limitValueStr);
            }
            case RiskManagementGroupEnum.MaxOrderAmount:
                return new RowItemDataStringArray(curAccount.MaxOrderAmount === -1 ? '' : curAccount.MaxOrderAmount.toString());
            case RiskManagementGroupEnum.DayTraderPatternProtection:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.DayTraderPatternProtection, curAccount, assetBalance));
            case RiskManagementGroupEnum.AvailableDayTrades:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.AvailableDayTrades, curAccount, assetBalance));
            case RiskManagementGroupEnum.ProjectedBalance_MaxDrawdownLevel:
                if (isNaN(value.secondValue)) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.ProjectedBalance, curAccount, assetBalance), assetBalance.formatPrice(value.secondValue));
                }
            case RiskManagementGroupEnum.ProjectedBalance_MaxRelativeDrawDownLevel:
                if (value.secondValue <= 0) {
                    return new RowItemDataStringArray('', '');
                }
                else {
                    return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.ProjectedBalance, curAccount, assetBalance), Account.GetAccountFeatureString(value.secondValue, AccountFeature.MaxRelativeDrawDownLevel, curAccount, assetBalance));
                }
            case RiskManagementGroupEnum.LossLimitPerTrade:
                return new RowItemDataStringArray(value.firstValue === 0
                    ? ''
                    : Account.GetAccountFeatureString(value.firstValue, AccountFeature.LossLimitPerTrade, curAccount, assetBalance));
            case RiskManagementGroupEnum.EODTrailingDrawdownLevel:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.EODTrailingDrawdownLevel, curAccount, assetBalance));
            case RiskManagementGroupEnum.MaxPositionValuePerSymbol: {
                if (isValidNumber(value.firstValue) && isValidNumber(value.secondValue)) {
                    const parsedKey = this.parseInstrumentGroupRiskPlanItemKey(key);
                    const riskPlanItem = this._riskPlanHelper.findRiskPlanItem(this.Account.RiskPlan, MarginTypes.TIERED_BY_GROUP, parsedKey.routeId, parsedKey.instrumentGroupId, parsedKey.productType);
                    const formatAsset = (_a = DataCache.GetAssetById(riskPlanItem.PositionValueCurrencyId)) !== null && _a !== void 0 ? _a : this.AssetBalance.Asset;
                    return new RowItemDataStringArray(formatAsset.formatPrice(value.firstValue), formatAsset.formatPrice(value.secondValue));
                }
                else {
                    return new RowItemDataStringArray('');
                }
            }
            default:
                return new RowItemDataStringArray('');
        }
    }
    getItemNameKey(type) {
        switch (type) {
            case RiskManagementGroupEnum.CurrentStatus:
                return 'panel.accounts.CurrentStatus';
            case RiskManagementGroupEnum.AllowTradingPrePost:
                return 'panel.accounts.AllowTradingPrePost';
            case RiskManagementGroupEnum.DailyLoss_MaxDailyLossLimit:
                return 'panel.accounts.MaxDailyLossLimit';
            case RiskManagementGroupEnum.CurrentOrdersCountPerDay_MaxOrdersPerDay:
                return 'panel.accounts.MaxOrdersPerDay';
            case RiskManagementGroupEnum.CurrentPositions_MaxPositions:
                return 'panel.accounts.MaxPositions';
            case RiskManagementGroupEnum.CurrentPendingOrders_MaxPendingOrders:
                return 'panel.accounts.MaxPendingOrders';
            case RiskManagementGroupEnum.MaxPendingOrdersValue:
                return 'panel.accounts.MaxPendingOrdersValue';
            case RiskManagementGroupEnum.MaxOrderCapital:
                return 'panel.accounts.MaxOrderCapital';
            case RiskManagementGroupEnum.MaximumLot:
                return 'panel.accounts.MaximumLot';
            case RiskManagementGroupEnum.MaxPositionQtyPerSymbol:
                return 'panel.accounts.MaxPositionQtyPerSymbol';
            case RiskManagementGroupEnum.AllowOvernightTrading:
                return 'panel.accounts.AllowOvernightTrading';
            case RiskManagementGroupEnum.ProjectedBalance_TrailingDrawdownLevel:
                return 'panel.accounts.TrailingDrawdownLevel';
            case RiskManagementGroupEnum.CurrentTotalPositionQty_TotalMaxPositionsQty:
                return 'panel.accounts.TotalMaxPositionsQty';
            case RiskManagementGroupEnum.CurrentWeeklyLoss_MaxWeekLostLimit:
                return 'panel.accounts.MaxWeekLostLimit';
            case RiskManagementGroupEnum.UnrealizedLoss_UnrealizedLossLimit:
                return 'panel.accounts.UnrealizedLossLimit';
            case RiskManagementGroupEnum.MaxDailyProfit:
                return 'panel.accounts.MaxDailyProfit';
            case RiskManagementGroupEnum.VolumeLimit:
                return 'panel.accounts.VolumeLimit';
            case RiskManagementGroupEnum.MaxOrderAmount:
                return 'panel.accounts.MaxOrderAmount';
            case RiskManagementGroupEnum.DayTraderPatternProtection:
                return 'panel.accounts.DayTraderPatternProtection';
            case RiskManagementGroupEnum.AvailableDayTrades:
                return 'panel.accounts.AvailableDayTrades';
            case RiskManagementGroupEnum.ProjectedBalance_MaxRelativeDrawDownLevel:
                return 'panel.accounts.MaxRelativeDrawDownLevel';
            case RiskManagementGroupEnum.LossLimitPerTrade:
                return 'panel.accounts.LossLimitPerTrade';
            case RiskManagementGroupEnum.EODTrailingDrawdownLevel:
                return 'panel.accounts.EODTrailingDrawdownLevel';
            case RiskManagementGroupEnum.DailyLoss_RelativeDailyLossLimit:
                return 'panel.accounts.RelativeDailyLossLimit';
            case RiskManagementGroupEnum.ProjectedBalance_MaxDrawdownLevel:
                return 'panel.accounts.MaxDrawdownLevel';
            case RiskManagementGroupEnum.MaxPositionValuePerSymbol:
                return 'panel.accounts.MaxPositionValuePerSymbol';
        }
    }
    getItemName(type, key) {
        var _a, _b;
        const name = super.getItemName(type, key);
        switch (type) {
            case RiskManagementGroupEnum.MaxPositionValuePerSymbol: {
                const parsedKey = this.parseInstrumentGroupRiskPlanItemKey(key);
                const item = parsedKey != null
                    ? this._riskPlanHelper.findRiskPlanItem(this.Account.RiskPlan, MarginTypes.TIERED_BY_GROUP, parsedKey.routeId, parsedKey.instrumentGroupId, parsedKey.productType)
                    : null;
                if (item != null) {
                    const instrumentGroupName = (_b = (_a = DataCache.getInstrumentTypeById(parsedKey.instrumentGroupId)) === null || _a === void 0 ? void 0 : _a.Name) !== null && _b !== void 0 ? _b : '';
                    const prefix = isValidString(instrumentGroupName) ? `${instrumentGroupName} ` : '';
                    const postfix = item.ProductType !== ProductType.General
                        ? ` ${Resources.getResource('ProductType.' + getProductTypeKey(item.ProductType))}`
                        : '';
                    return `${prefix}${name}${postfix}`;
                }
                else {
                    return name;
                }
            }
            case RiskManagementGroupEnum.VolumeLimit: {
                const parsedKey = this.parseInstrumentTypeItemKey(key);
                if (!isNullOrUndefined(parsedKey)) {
                    return Resources.getResource(this._volumeLimitsHelper.getLocalizationKey(parsedKey));
                }
                else {
                    return name;
                }
            }
            default:
                return name;
        }
    }
    getItemTooltip(type, key) {
        if (type === RiskManagementGroupEnum.VolumeLimit) {
            const parsedKey = this.parseInstrumentTypeItemKey(key);
            if (!isNullOrUndefined(parsedKey)) {
                return Resources.getResource(this._volumeLimitsHelper.getTooltipLocalizationKey(parsedKey));
            }
        }
        return super.getItemTooltip(type, key);
    }
    getItemRule(type) {
        switch (type) {
            case RiskManagementGroupEnum.CurrentStatus:
                return 'FUNCTION_ACCOUNT_DETAILS_TRADING_STATUS';
            case RiskManagementGroupEnum.DailyLoss_MaxDailyLossLimit:
                return 'FUNCTION_ACCOUNT_DETAILS_DAILY_LOSS_LIMIT';
            case RiskManagementGroupEnum.DailyLoss_RelativeDailyLossLimit:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_RELATIVE_DAILY_LOSS_LIMIT';
            case RiskManagementGroupEnum.CurrentWeeklyLoss_MaxWeekLostLimit:
                return 'FUNCTION_ACCOUNT_DETAILS_WEEKLY_LOSS_LIMIT';
            case RiskManagementGroupEnum.UnrealizedLoss_UnrealizedLossLimit:
                return 'FUNCTION_ACCOUNT_DETAILS_UNREALIZED_LOSS_LIMIT';
            case RiskManagementGroupEnum.ProjectedBalance_TrailingDrawdownLevel:
                return 'FUNCTION_ACCOUNT_DETAILS_TRAILING_DRAWDOWN';
            case RiskManagementGroupEnum.CurrentTotalPositionQty_TotalMaxPositionsQty:
                return 'FUNCTION_ACCOUNT_DETAILS_TOTAL_MAX_POSITION';
            case RiskManagementGroupEnum.CurrentOrdersCountPerDay_MaxOrdersPerDay:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_ORDER_COUNT';
            case RiskManagementGroupEnum.CurrentPositions_MaxPositions:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_POSITIONS';
            case RiskManagementGroupEnum.CurrentPendingOrders_MaxPendingOrders:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_PENDING_ORDERS';
            case RiskManagementGroupEnum.MaxOrderCapital:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_ORDER_CAPITAL';
            case RiskManagementGroupEnum.VolumeLimit:
                return 'FUNCTION_ACCOUNT_DETAILS_EQUITIES_VOLUME_LIMIT';
            case RiskManagementGroupEnum.ProjectedBalance_MaxDrawdownLevel:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_DRAWDOWN';
            case RiskManagementGroupEnum.MaxDailyProfit:
                return 'FUNCTION_ACCOUNT_DETAILS_DAILY_PROFIT_TARGET';
            case RiskManagementGroupEnum.MaxOrderAmount:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_ORDER_AMOUNT';
            case RiskManagementGroupEnum.UnrealizedLoss:
                return 'FUNCTION_ACCOUNT_DETAILS_UNREALIZED_LOSS';
            case RiskManagementGroupEnum.DayTraderPatternProtection:
                return 'FUNCTION_ACCOUNT_DETAILS_DAY_TRADER_PATTERN';
            case RiskManagementGroupEnum.AvailableDayTrades:
                return 'FUNCTION_ACCOUNT_DETAILS_AVAILABLE_DAY_TRADES';
            case RiskManagementGroupEnum.ProjectedBalance_MaxRelativeDrawDownLevel:
                return 'FUNCTION_ACCOUNT_DETAILS_MAX_RELATIVE_DRAWDOWN';
            case RiskManagementGroupEnum.LossLimitPerTrade:
                return 'FUNCTION_ACCOUNT_DETAILS_LOSS_LIMIT_PER_TRADES';
            case RiskManagementGroupEnum.EODTrailingDrawdownLevel:
                return 'FUNCTION_ACCOUNT_DETAILS_EOD_TRAILING_DRAWDOWN';
            default:
                return null;
        }
    }
    getItemGroupRule() {
        return GROUP.RISK_MANAGEMENT;
    }
    isHiddenItemRow(type) {
        if (super.isHiddenItemRow(type)) {
            return true;
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
            return true;
        }
        const account = this.Account;
        if (type === RiskManagementGroupEnum.DayTraderPatternProtection ||
            type === RiskManagementGroupEnum.AvailableDayTrades) {
            return !account.DayTraderPattern;
        }
    }
    getColoringMethod(type) {
        return VerticalPanelColoringTypes.Default;
    }
    createDynamicItemsFromInstrumentGroupItems(type, items, match) {
        const dynamicItems = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            for (const [productType, riskPlanItem] of item.itemsByProductType) {
                if (!match(item, riskPlanItem)) {
                    continue;
                }
                const key = this.createInstrumentGroupRiskPlanItemKey(item.routeId, item.isSuperGroup, item.groupId, productType);
                const groupDynamicItem = {
                    type,
                    key
                };
                dynamicItems.push(groupDynamicItem);
            }
            if (item.isSuperGroup) {
                const childItems = this.createDynamicItemsFromInstrumentGroupItems(type, item.childGroups, match);
                dynamicItems.push(...childItems);
            }
        }
        return dynamicItems;
    }
    createInstrumentGroupRiskPlanItemKey(routeId, isSuperGroup, instrumentGroupId, productType) {
        return `${routeId}_${isSuperGroup}_${instrumentGroupId}_${productType}`;
    }
    parseInstrumentGroupRiskPlanItemKey(key) {
        const parts = key.split('_');
        if (parts.length !== 4) {
            return null;
        }
        const parsedRouteId = parseInt(parts[0]);
        const parsedIsSuperGroup = parts[1] === 'true';
        const parsedInstrumentGroupId = parseInt(parts[2]);
        const parsedProductType = parseInt(parts[3]);
        if (!isValidNumber(parsedRouteId) ||
            isNullOrUndefined(parsedIsSuperGroup) ||
            !isValidNumber(parsedInstrumentGroupId) ||
            !isValidNumber(parsedProductType)) {
            return null;
        }
        return {
            routeId: parsedRouteId,
            isSuperGroup: parsedIsSuperGroup,
            instrumentGroupId: parsedInstrumentGroupId,
            productType: parsedProductType
        };
    }
    createDynamicItemsFromInstrumentType(type, instrumentTypes) {
        const dynamicItems = [];
        for (let i = 0; i < instrumentTypes.length; i++) {
            const groupDynamicItem = {
                type,
                key: instrumentTypes[i].toString()
            };
            dynamicItems.push(groupDynamicItem);
        }
        return dynamicItems;
    }
    parseInstrumentTypeItemKey(key) {
        const instrumentType = parseInt(key);
        if (!isValidNumber(instrumentType)) {
            return null;
        }
        return instrumentType;
    }
}
